/**
 * `/profile` page data layer (Plan 04).
 *
 * Bootstrap reads the central User row + notification preference matrix
 * in one round-trip. Writes are debounced/per-toggle from the cards.
 *
 * The `useProfile()` store in `@/stores/profile` is separate — it
 * bootstraps the SPA shell from `/api/me/profile` (resolved nav +
 * permissions). This module is for the deeper "About you" surface.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiFetch } from './http'
import type { NotificationCategory, NotificationChannel } from './notifications'

// ---------------------------------------------------------------------------
// Types — kept identical to the previous GraphQL surface so page code
// migrates without semantic changes.
// ---------------------------------------------------------------------------

export interface MeProfileNotificationPreference {
  id: string
  category: NotificationCategory
  channel: NotificationChannel
  enabled: boolean
}

export interface MeProfile {
  id: string
  email: string | null
  firstName: string
  lastName: string
  preferredName: string | null
  pronouns: string | null
  phoneNumber: string | null
  locale: string
  avatarUri: string | null
  effectivePermissions: string[]
  notificationPreferences: MeProfileNotificationPreference[]
}

export interface MeProfileResponse {
  me: MeProfile | null
}

export interface UpdateMyProfileVariables {
  firstName?: string | null
  lastName?: string | null
  preferredName?: string | null
  pronouns?: string | null
  phoneNumber?: string | null
  locale?: string | null
}

export interface UpdateMyProfileResponse {
  id: string
  firstName: string
  lastName: string
  preferredName: string | null
  pronouns: string | null
  phoneNumber: string | null
  locale: string
}

export interface UpdateMyNotificationPreferenceVariables {
  category: NotificationCategory
  channel: NotificationChannel
  enabled: boolean
}

export interface UpdateMyNotificationPreferenceResponse {
  id: string
  category: NotificationCategory
  channel: NotificationChannel
  enabled: boolean
}

export interface RequestEmailChangeVariables {
  newEmail: string
}

export interface ConfirmEmailChangeVariables {
  token: string
}

/** `POST /api/me/email-change/confirm` — the updated sign-in email. */
export interface ConfirmEmailChangeResponse {
  email: string
}

/** `GET /api/me/sign-in-methods` — drives the Security & sign-in card. */
export interface SignInMethods {
  hasPassword: boolean
  googleLinked: boolean
}

export interface SetPasswordVariables {
  newPassword: string
  currentPassword?: string
}

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

export async function fetchMeProfile(signal?: AbortSignal): Promise<MeProfileResponse> {
  return apiFetch<MeProfileResponse>('/api/me', { signal })
}

export async function updateMyProfile(vars: UpdateMyProfileVariables): Promise<UpdateMyProfileResponse> {
  return apiFetch<UpdateMyProfileResponse>('/api/me', { method: 'PATCH', body: vars })
}

export async function updateMyNotificationPreference(
  vars: UpdateMyNotificationPreferenceVariables,
): Promise<UpdateMyNotificationPreferenceResponse> {
  return apiFetch<UpdateMyNotificationPreferenceResponse>('/api/me/notification-preferences', {
    method: 'PUT',
    body: vars,
  })
}

export async function requestEmailChange(vars: RequestEmailChangeVariables): Promise<void> {
  await apiFetch<void>('/api/me/email-change', { method: 'POST', body: vars })
}

export async function confirmEmailChange(vars: ConfirmEmailChangeVariables): Promise<ConfirmEmailChangeResponse> {
  return apiFetch<ConfirmEmailChangeResponse>('/api/me/email-change/confirm', { method: 'POST', body: vars })
}

export async function fetchSignInMethods(signal?: AbortSignal): Promise<SignInMethods> {
  return apiFetch<SignInMethods>('/api/me/sign-in-methods', { signal })
}

export async function setPassword(vars: SetPasswordVariables): Promise<void> {
  await apiFetch<{ ok: true }>('/api/me/password', { method: 'POST', body: vars })
}

// ---------------------------------------------------------------------------
// Query / mutation hooks
// ---------------------------------------------------------------------------

export const meProfileQueryKey = ['me', 'profile'] as const

export function useMeProfileQuery() {
  return useQuery<MeProfileResponse>({
    queryKey: meProfileQueryKey,
    queryFn: ({ signal }) => fetchMeProfile(signal),
  })
}

export function useUpdateMyProfileMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: updateMyProfile,
    onSuccess: (updated) => {
      // Reconcile the cached MeProfile with the partial update so the
      // IdentityCard re-renders with the persisted values immediately.
      qc.setQueryData<MeProfileResponse | undefined>(meProfileQueryKey, (prev) => {
        if (!prev?.me) {
          return prev
        }
        return {
          ...prev,
          me: {
            ...prev.me,
            firstName: updated.firstName,
            lastName: updated.lastName,
            preferredName: updated.preferredName,
            pronouns: updated.pronouns,
            phoneNumber: updated.phoneNumber,
            locale: updated.locale,
          },
        }
      })
    },
  })
}

export function useUpdateMyNotificationPreferenceMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: updateMyNotificationPreference,
    onMutate: async (vars) => {
      // Optimistic update — flip the cell in the cached preference
      // matrix so the Switch reflects the new state before the
      // round-trip completes. Mirrors the Apollo `optimisticResponse`
      // we previously had on this mutation.
      await qc.cancelQueries({ queryKey: meProfileQueryKey })
      const previous = qc.getQueryData<MeProfileResponse>(meProfileQueryKey)
      qc.setQueryData<MeProfileResponse | undefined>(meProfileQueryKey, (prev) => {
        if (!prev?.me) {
          return prev
        }
        const idx = prev.me.notificationPreferences.findIndex(
          (p) => p.category === vars.category && p.channel === vars.channel,
        )
        const next = [...prev.me.notificationPreferences]
        if (idx >= 0) {
          const existing = next[idx]!
          next[idx] = { ...existing, enabled: vars.enabled }
        } else {
          next.push({
            id: `optimistic-${vars.category}-${vars.channel}`,
            category: vars.category,
            channel: vars.channel,
            enabled: vars.enabled,
          })
        }
        return {
          ...prev,
          me: {
            ...prev.me,
            notificationPreferences: next,
          },
        }
      })
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      // Rollback on failure.
      if (ctx?.previous) {
        qc.setQueryData(meProfileQueryKey, ctx.previous)
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: meProfileQueryKey })
    },
  })
}

export function useRequestEmailChangeMutation() {
  return useMutation({
    mutationFn: requestEmailChange,
  })
}

export function useConfirmEmailChangeMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: confirmEmailChange,
    onSuccess: () => {
      // The sign-in email changed — refetch the profile surfaces that show it.
      void qc.invalidateQueries({ queryKey: meProfileQueryKey })
    },
  })
}

export const signInMethodsQueryKey = ['me', 'sign-in-methods'] as const

export function useSignInMethodsQuery() {
  return useQuery<SignInMethods>({
    queryKey: signInMethodsQueryKey,
    queryFn: ({ signal }) => fetchSignInMethods(signal),
  })
}

export function useSetPasswordMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: setPassword,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: signInMethodsQueryKey })
    },
  })
}
